/* This example requires Tailwind CSS v2.0+ */
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { buildTimestamp } from "../../../helpers/getTimestamp";
import { getSubscriptionTeamMembers } from "../../../services/clientServiceSubscription/getSubscriptionTeamMembers";
import ButtonAddTimestamp from "../../../utils/ButtonAddTimestamp";
import NotesWithMentions from "../../notes/NotesWithMentions";
import AssignedByListBox from "./AssignedByListBox";
import ButtonModal from "./ButtonModal";
import ErrorMessage from "./ErrorMessage";
import InputDateModal from "./InputDateModal";
import { usePermissions } from "../../../context/PermissionsContext";

const AssignNewEmployeeToJobModal = ({
    open,
    setOpen,
    nextJob,
    setNextjob,
    loggedInEmployee,
    updateChecklistItemAndJob,
    initializeJobs,
    onHandleCancelFromParent,
    clientService,
    getNewTasks,
}) => {
    const { can, permissionsLoading } = usePermissions();
    const canWriteSteps = can("steps.write");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [displayNotes, setDisplayNotes] = useState(false);
    const [errors, setErrors] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);

    let completeButtonRef = useRef(null);

    useEffect(() => {
        console.log("nextJob", nextJob);
    }, []);

    const toggleNotes = () => {
        setDisplayNotes(!displayNotes);
    };

    const onHandleCancel = () => {
        setErrors([]);
        initializeJobs();
        setOpen(false);
        if (typeof getNewTasks === "function") {
            // Important: pull canonical state from backend so no checkbox
            // remains toggled optimistically after cancel.
            getNewTasks();
        }
        onHandleCancelFromParent();
    };

    const onHandleChangeAssignedEmployees = (newAssignedEmployees) => {
        console.log(nextJob);
        setNextjob({
            ...nextJob,
            assignedEmployees: [newAssignedEmployees],
            assignedDate: moment().format("YYYY-MM-DD"),
        });
    };

    const onHandleChange = (e) => {
        console.log(e.target.name, e.target.value);
        const fieldName = e.target.name;
        const fieldValue = e.target.value;
        setNextjob({ ...nextJob, [fieldName]: fieldValue });
    };

    const onHandleChangeNotes = (newNotes, newMentionedEmployees) => {
        setNextjob({
            ...nextJob,
            notes: newNotes,
            mentionedEmployeesIds: newMentionedEmployees,
        });
    };

    const validateForm = (form) => {
        if (form.assignedEmployees.length < 1) {
            setErrors({ assignedEmployees: "This field cannot be empty." });
            return false;
        }
        if (form.dueDate === "0001-01-01T00:00:00") {
            setErrors({ dueDate: "You need to choose a due date." });
            return false;
        }
        return true;
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteSteps || isSubmitting) return;
        console.log(nextJob);
        if (!validateForm(nextJob)) {
            return;
        }
        setIsSubmitting(true);
        try {
            await updateChecklistItemAndJob(nextJob);
            setErrors([]);
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateAssignedEmployee = (assignedEmployees) => {
        if (Array.isArray(assignedEmployees)) {
            if (assignedEmployees.length > 0) {
                return assignedEmployees[0];
            } else {
                return null;
            }
        } else if (assignedEmployees != null) {
            return assignedEmployees;
        } else {
            return null;
        }
    };

    const addTimestamp = () => {
        setNextjob({
            ...nextJob,
            notes:
                (nextJob.notes !== null ? nextJob.notes : "") +
                buildTimestamp(loggedInEmployee),
        });
    };

    useEffect(() => {
        if (permissionsLoading || !canWriteSteps || !clientService?.id) return;
        const getTeamMembers = async () => {
            const response = await getSubscriptionTeamMembers(clientService?.id);
            console.log("team-members", response);
            setTeamMembers(response.data.teamMembers);
        };
        getTeamMembers();
    }, [clientService?.id, open, canWriteSteps, permissionsLoading]);

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={() => {
                        setOpen(false);
                        onHandleCancel();
                    }}
                    className="relative z-50"
                >
                    {/* The backdrop, rendered as a fixed sibling to the panel container */}
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    {/* Full-screen scrollable container */}
                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        {/* Container to center the panel */}
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-lg sm:p-6">
                                <Dialog.Title className="w-full text-lg font-medium leading-6 text-center text-gray-900">
                                    Assign step {nextJob.name}
                                </Dialog.Title>

                                <form onSubmit={onHandleSubmit}>
                                    <AssignedByListBox
                                        label="Employee"
                                        employees={teamMembers}
                                        assignedBy={validateAssignedEmployee(
                                            nextJob.assignedEmployees,
                                        )}
                                        onChange={onHandleChangeAssignedEmployees}
                                    />
                                    {
                                        <ErrorMessage
                                            error={errors["assignedEmployees"]}
                                        />
                                    }

                                    {nextJob.assignedEmployees.length > 0 && (
                                        <>
                                            <InputDateModal
                                                label={"Due Date"}
                                                name="dueDate"
                                                onChange={onHandleChange}
                                                value={nextJob.dueDate}
                                            />
                                            <ErrorMessage
                                                error={errors["dueDate"]}
                                            />
                                        </>
                                    )}

                                    {!displayNotes ? (
                                        <button
                                            className="inline-flex items-center p-1 mt-4 text-base text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                            onClick={toggleNotes}
                                            type="button"
                                        >
                                            Add notes
                                        </button>
                                    ) : (
                                        <button
                                            className="inline-flex items-center p-1 mt-4 text-base text-white bg-red-500 border border-transparent rounded-sm shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            onClick={toggleNotes}
                                            type="button"
                                        >
                                            Hide notes
                                        </button>
                                    )}

                                    {displayNotes && (
                                        <>
                                            <NotesWithMentions
                                                label="Notes"
                                                notes={
                                                    nextJob.notes !== null
                                                        ? nextJob.notes
                                                        : ""
                                                }
                                                onHandleChangeNotes={
                                                    onHandleChangeNotes
                                                }
                                            />
                                            <ButtonAddTimestamp
                                                onHandleClick={() => addTimestamp()}
                                            />
                                        </>
                                    )}

                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <ButtonModal
                                            name={
                                                isSubmitting ? "Saving..." : "Save"
                                            }
                                            type="submit"
                                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-500 border border-transparent rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-teal-500 sm:col-start-2 sm:text-sm"
                                            disabled={
                                                permissionsLoading ||
                                                !canWriteSteps ||
                                                isSubmitting
                                            }
                                        />
                                        <ButtonModal
                                            name="Cancel"
                                            type="button"
                                            onClick={() => {
                                                onHandleCancel();
                                            }}
                                            className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        />
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};
export default AssignNewEmployeeToJobModal;
